import React, { useEffect } from 'react';
import { useForm, FormContextValues } from 'react-hook-form';
import {
  Platform,
  StyleSheet,
  View,
  Text,
  TouchableOpacity,
} from 'react-native';
import { useMediaQuery } from 'react-responsive';

import {
  PEPS_STATUS,
  PEPProps,
  PEP_FORM_FIELD_LABELS,
  PEP_FORM_FIELD_NAMES,
  PEP_FORM_FIELD_PLACEHOLDERS,
} from 'shared/constants/ekyc';
import Button from 'shared/modules/common/components/atoms/Button';
import Picker from 'shared/modules/common/components/molecules/Picker';
import generateID from 'shared/modules/common/utils/generate-id';
import validator, { Rule } from 'shared/utils/validator';

type Props = {
  onNextButtonPress: () => void;
  onReturnButtonPress: () => void;
  onPEPInfoButtonPress: () => void;
  form: FormContextValues<PEPProps>;
  onFocus?: () => void;
  onBlur?: () => void;
  onFieldBlur?: (field: 'pepStatus') => void;
};

const PEP = (props: Props) => {
  const isBigScreen = useMediaQuery({ minWidth: 768 });
  const {
    onNextButtonPress,
    onReturnButtonPress,
    onPEPInfoButtonPress,
    form,
    onFocus,
    onBlur,
    onFieldBlur,
  } = props;

  useEffect(() => {
    onFocus?.();

    return () => onBlur?.();
  }, []);

  return (
    <View style={styles.root}>
      <View style={styles.panel}>
        <Text style={styles.notifTitle}>{`外国PEPsに関する確認`}</Text>
        <Text style={styles.notifBody}>
          {`「犯罪収益移転防止法」に基づき、以下のお客様は本サービスをご利用になれません。\n\n`}
        </Text>
        <Text style={styles.notifBody}>
          {`・外国の政府等で重要な地位の方（外国の国家元首等）\n・過去にその地位にあった方 \n・その方のご家族　等\n\n`}
        </Text>
        <Text style={styles.notifBody}>
          外国PEPsについては、詳しくは
          <TouchableOpacity
            {...generateID('ekyc-pep-form-pep-info-button')}
            onPress={() => onPEPInfoButtonPress()}
          >
            <Text style={styles.link}>外国PEPsとは</Text>
          </TouchableOpacity>
          をご覧ください。
        </Text>
        <Text style={styles.notifBody}>
          {`\n\n外国PEPでない方は「該当しない」を選択してください。`}
        </Text>
      </View>
      <View style={styles.panel}>
        <View
          style={styles.form}
          {...Platform.select({ web: { accessibilityRole: 'form' } })}
        >
          {PEP_FORM_FIELD_NAMES.map((field, index) => (
            <Picker
              key={index}
              value={form.getValues(field)}
              label={PEP_FORM_FIELD_LABELS[index]}
              placeholder={PEP_FORM_FIELD_PLACEHOLDERS[index]}
              options={PEPS_STATUS.map((place) => ({
                label: place,
                value: place,
              }))}
              selectButtonText="保存する"
              onSelect={(text) => form.setValue(field, text)}
              onBlur={() => {
                onFieldBlur?.(field);
                form.triggerValidation(field);
              }}
              message={(form.errors[field]?.message as string) || ''}
              state={form.errors[field]?.message ? 'error' : undefined}
              dialogStyle={styles.pickerDialog}
              showAsDialog={isBigScreen}
              id={`ekyc-pep-form-${field}-picker`}
            />
          ))}
        </View>
      </View>
      <View
        style={[
          styles.buttonContainer,
          isBigScreen && styles.buttonContainerBigScreen,
        ]}
      >
        <Button
          title="次へ"
          onPress={async () => {
            const isValid = await form.triggerValidation();

            if (isValid) {
              onNextButtonPress();
            }
          }}
          id="ekyc-pep-form-next-button"
          disabled={!form.formState.dirty}
        />
        {isBigScreen && (
          <Button
            onPress={onReturnButtonPress}
            title="戻る"
            variant="outlined"
            style={styles.returnButton}
            id="ekyc-pep-form-return-button"
          />
        )}
      </View>
    </View>
  );
};

export const useFormPEPWithValidations = (props: PEPProps) => {
  const form = useForm<PEPProps>({
    defaultValues: props,
  });

  const basicRules: Rule[] = [
    'mustNotBeEmptyOrSpace',
    'mustNotContainSpace',
    'mustNotContainNonPrintableChars',
  ];

  useEffect(() => {
    PEP_FORM_FIELD_NAMES.forEach((name) => {
      form.register(
        { name },
        {
          validate: validator.getRules(basicRules),
        }
      );
    });
  }, [form.register]);

  form.watch();

  return form;
};

const styles = StyleSheet.create({
  root: {
    flexDirection: 'column',
    flexGrow: 1,
  },
  panel: {
    padding: 24,
    borderRadius: 16,
    backgroundColor: '#fff',
    marginVertical: 12,
  },
  notifTitle: {
    fontSize: 14,
    color: '#FF2751',
    fontWeight: '600',
    marginBottom: 16,
  },
  notifBody: {
    fontSize: 13,
    color: '#1C1C1C',
  },
  link: {
    color: '#E52581',
    fontSize: 13,
  },
  form: {
    flex: 1,
    justifyContent: 'space-between',
  },
  pickerDialog: {
    width: 424,
  },
  buttonContainer: {
    flex: 1,
    flexDirection: 'column',
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingTop: 12,
  },
  buttonContainerBigScreen: {
    paddingTop: 48,
  },
  returnButton: {
    marginTop: 18,
    paddingVertical: 8,
    paddingHorizontal: 8,
  },
});

export default PEP;
